import React, { useContext, useState } from 'react';

import cn from 'classnames';
import { format } from 'date-fns';

import CommentActionUI from './CommentActions';
import FlagButton from './FlagButton';
import ReplyForm from './ReplyForm';
import { useKhorosUser } from '../../../apis/khoros/hooks';
import { KhorosReplyItemSchema } from '../../../apis/khoros/type/khoros-replies.type';
import { useUserProfile } from '../../../apis/useUserSearch';
import { ConfigContext } from '../../../aurr/contexts/ConfigContext';
import I18nContext from '../../../aurr/contexts/I18nContext';
import { ProfileAvatar } from '../../container/user';
import { ProfileName } from '../../container/user/User';

interface ReplyProps {
  parentId: string;
  baseURL?: string;
  userContentURL?: string;
  userProfileURL?: string;
  topicId: string;
  comment: KhorosReplyItemSchema;
}

export default function ReplyUI({
  parentId,
  baseURL,
  userContentURL,
  userProfileURL,
  topicId,
  comment,
}: ReplyProps) {
  const {
    kh_author_sso_id,
    kh_body,
    kh_post_time,
    kh_author_user_id,
    kh_author_avatar,
    kh_post_id,
    isOptimistic,
  } = comment;

  const { t } = useContext(I18nContext);

  const { USER_PROFILE_URL, USER_CONTENT_URL, KHOROS_API } =
    useContext(ConfigContext);
  const [isEditing, setIsEditing] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [isHover, setIsHover] = useState(false);

  const { data: myKhorosUser } = useKhorosUser({
    baseURL: baseURL || KHOROS_API,
  });

  const { isLoading: profileIsLoading, data: user } = useUserProfile({
    oxygenId: kh_author_sso_id,
    userProfileURL: userProfileURL || USER_PROFILE_URL,
    userContentURL: userContentURL || USER_CONTENT_URL,
  });

  const { firstName, lastName, userName, encryptedId } = user || {};

  const parsedName =
    firstName && lastName ? `${firstName} ${lastName}` : userName;

  const { kh_user_id: myUserId } = myKhorosUser || {};

  const profileURL =
    !profileIsLoading && encryptedId
      ? `/autodesk-university/profile/${encodeURIComponent(encryptedId)}`
      : '';

  const parsedBody =
    kh_body?.replace(/(\\r)|(\\n)/g, '\n')?.replace(/(<([^>]+)>)/gi, '') || '';

  const replyInProgress = isOptimistic || kh_post_id === 'temporary-value';

  const isAuthor =
    !!(kh_author_user_id && myUserId) && kh_author_user_id === myUserId;

  const closeEditing = () => {
    setIsEditing(false);
    setShowEditForm(false);
  };

  const handleMouseIn = () => {
    setIsHover(true);
  };

  const handleMouseOut = () => {
    setIsHover(false);
  };

  return (
    <>
      {showEditForm && myUserId ? (
        <ReplyForm
          parentId={parentId}
          topicId={topicId}
          baseURL={baseURL}
          userContentURL={userContentURL}
          userProfileURL={userProfileURL}
          onEditTentative={() => {
            setIsEditing(false);
          }}
          onEditSuccess={closeEditing}
          onEditFail={() => {
            setIsEditing(true);
          }}
          comment={comment}
        />
      ) : null}
      {!isEditing && (
        <div
          className={cn('au-flex au-gap-2 au-items-start au-pl-12', {
            'au-opacity-30': replyInProgress,
          })}
          onMouseEnter={handleMouseIn}
          onMouseLeave={handleMouseOut}
        >
          <div>
            <ProfileAvatar
              alt={t('avatarFor', { ns: 'common', fullName: parsedName })}
              size="small"
              src={kh_author_avatar}
              fullName={parsedName}
              isLoading={!myKhorosUser && profileIsLoading}
              oxygenId={kh_author_sso_id}
            />
          </div>
          <div className="au-text-c14 au-grow au-relative">
            <ProfileName
              isLoading={profileIsLoading}
              fullName={parsedName}
              hasLink
              profileURL={profileURL}
              className="au-text-link-text"
            />
            <p className="au-whitespace-pre-wrap au-break-words">
              {parsedBody}
            </p>
            <span className="au-text-c12 au-text-gray-300">
              {format(new Date(kh_post_time), 'PP')}
            </span>
            <FlagButton
              baseURL={baseURL}
              commentId={kh_post_id}
              body={kh_body}
            />
            {isHover && !replyInProgress ? (
              <CommentActionUI
                isAuthor={isAuthor}
                onEdit={() => {
                  setIsEditing(true);
                  setShowEditForm(true);
                }}
              />
            ) : null}
          </div>
        </div>
      )}
    </>
  );
}
